import React, { useEffect, useState } from 'react';
import { is } from '../../services';
import { Icon, IconName } from '../icon';
import { hashClass } from '../../helpers';
import clsx from 'clsx';
import { useTrilogyContext } from '../../context';
/**
 * Pagination Component
 * @param count {number} Number elements
 * @param defaultPage {number} Current default active page (default is 1)
 * @param pageSize {number} Element per page (default is 10)
 * @param onClick {Function} Return pagination object
 * * - -------------------------- WEB PROPERTIES -------------------------------
 * @param className {string} Additionnal css classes
 * @param href {Function} Function that generates a link for seo bots
 * @param routerLink Custom Router Link as props
 */
const Pagination = ({ className, count, defaultPage = 1, pageSize = 10, onClick, testId, href, routerLink: Link = 'a', ...others }) => {
    const [currentPage, setCurrentPage] = useState(defaultPage);
    const { styled } = useTrilogyContext();
    const classes = hashClass(styled, clsx('pagination', is('rounded'), is('centered'), className));
    const pager = React.useMemo(() => {
        // Calculate total pages
        const totalPages = Math.ceil(count / pageSize);
        let startPage = 1;
        let endPage;
        if (totalPages <= 5) {
            // less than pageSize(default is 5) total pages so show all
            startPage = 1;
            endPage = totalPages;
        }
        else {
            // more than 3 total pages so calculate start and end pages
            if (currentPage <= 3) {
                startPage = 1;
                endPage = 5;
            }
            else if (currentPage + 3 >= totalPages) {
                startPage = totalPages - 4;
                endPage = totalPages;
            }
            else {
                startPage = currentPage - 2;
                endPage = currentPage + 2;
            }
        }
        // Create an array of pages
        const pages = [...Array(endPage + 1 - startPage).keys()].map((i) => startPage + i);
        // Set pager object
        return {
            currentPage,
            pageSize,
            totalPages,
            endPage,
            pages,
        };
    }, [currentPage, pageSize, count]);
    const onPageChange = (e, page) => {
        if (page < 1 || page > pager.totalPages)
            return;
        if (page === currentPage)
            return;
        pager.currentPage = page;
        setCurrentPage(page);
        if (onClick) {
            e.preventDefault();
            onClick(pager);
        }
    };
    const totalCountPages = count / pageSize;
    useEffect(() => {
        if (defaultPage !== undefined) {
            setCurrentPage(defaultPage);
        }
    }, [defaultPage]);
    return (React.createElement("nav", { "data-testid": testId, className: classes, ...others },
        React.createElement(Link, { className: hashClass(styled, clsx('pagination-previous')), "aria-label": 'Page pr\u00E9c\u00E9dente', "aria-disabled": currentPage === 1, onClick: (event) => {
                if (currentPage <= 1) {
                    event.preventDefault();
                    return;
                }
                onPageChange(event, currentPage - 1);
            }, href: href === null || href === void 0 ? void 0 : href(currentPage - 1) },
            React.createElement(Icon, { name: IconName.ARROW_LEFT })),
        React.createElement("ul", { className: hashClass(styled, clsx('pagination-list')) },
            !pager.pages.includes(1) && (React.createElement("li", null,
                React.createElement("span", { className: hashClass(styled, clsx('pagination-ellipsis')) }, "\u2026"))),
            pager.pages.map((pageNumber) => (React.createElement("li", { "data-testid": `${testId}_${pageNumber}`, key: pageNumber },
                React.createElement(Link, { className: hashClass(styled, clsx('pagination-link', currentPage === pageNumber && is('current'))), "aria-label": `Aller à la page ${pageNumber}`, onClick: (event) => {
                        onPageChange(event, pageNumber);
                    }, href: href === null || href === void 0 ? void 0 : href(pageNumber) }, pageNumber)))),
            !pager.pages.includes(totalCountPages) && (React.createElement("li", null,
                React.createElement("span", { className: hashClass(styled, clsx('pagination-ellipsis')) }, "\u2026")))),
        React.createElement(Link, { className: hashClass(styled, clsx('pagination-next')), "aria-label": 'Page suivante', "aria-disabled": currentPage === Math.max(pager.totalPages), onClick: (event) => {
                if (currentPage >= Math.max(pager.totalPages)) {
                    event.preventDefault();
                    return;
                }
                onPageChange(event, currentPage + 1);
            }, href: href === null || href === void 0 ? void 0 : href(currentPage + 1) },
            React.createElement(Icon, { name: IconName.ARROW_RIGHT }))));
};
export default Pagination;
//# sourceMappingURL=Pagination.js.map